import { useContext, useMemo } from 'react';
import { AdminListPageWrapper } from '../../AdminListPage';
import { AdminTable, AdminTableContext, DateCellFromString } from '../../AdminTable';
import { AdminMatchTeams, Cohorts, CorporateClients } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  AdminTeamMatchExportResponse,
  CardStatusEnum,
  TeamMatchDetails,
} from '@platform/client/schemas';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';
import { Link } from 'wouter';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { TeamLink } from '../teams/TeamsResultsTab';

export const TeamsMatchAdmin = () => {
  return (
    <AdminListPageWrapper>
      <TeamsMatchAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<TeamMatchDetails>();

const TeamsMatchAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { data, isError, isLoading, isSuccess } = AdminMatchTeams.useGetAdminTeamsMatchingData(
    {
      ...adminQueryOptionsV2,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const matchData = data?.matches || [];
  const adminMatchesCount = data?.totalCount || 0;

  const { data: corpClientFilterData } = CorporateClients.useGetAllCorporateClients();
  const { data: cohortsFilterData } = Cohorts.useGetCohorts();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('teamDiscoveryId', {
          cell: (teamDiscoveryId) => <TeamMatchLink discoveryId={teamDiscoveryId.getValue()} />,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Discovery ID</span>,
          size: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('team.id', {
          cell: ({ row }) => (
            <TeamLink teamId={row.original.team.id} cohortId={row.original.cohort.id} />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Team ID</span>,
          size: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('team.members', {
          cell: (members) => (
            <div className='flex gap-2 flex-wrap'>
              {members.getValue().map((member, index) => {
                const name = member.firstName + ' ' + member.lastName;
                const isLast = index === members.getValue().length - 1;
                return (
                  <span key={member.id}>
                    <ProfessionalLink userId={member.id} name={name} />
                    {!isLast && ','}
                  </span>
                );
              })}
            </div>
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Team Members</span>,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
          size: 150,
        }),
        columnHelper.accessor('corporateClient', {
          cell: (corporateClient) => corporateClient.getValue().name,
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Corporate Client</span>
          ),
          size: 150,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: corpClientFilterData?.map((client) => ({
              label: client.name,
              value: client.id,
            })),
          },
          enableSorting: true,
        }),
        columnHelper.accessor('cohort', {
          cell: (cohort) => (
            <Link
              to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.TEAMS}/${cohort.getValue().id}`}
              className='text-brand-500 hover:underline'
            >
              {cohort.getValue().name}
            </Link>
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Cohort</span>,
          size: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: cohortsFilterData?.cohorts.map((cohort) => ({
              label: cohort.name,
              value: cohort.id,
            })),
          },
          enableSorting: true,
        }),
        columnHelper.accessor('organization', {
          cell: (organization) => (
            <OrganizationLink
              organizationName={organization.getValue().name}
              organizationId={organization.getValue().id}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Organization</span>,
          size: 150,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('project', {
          cell: (project) => (
            <ProjectLink projectId={project.getValue().id} projectName={project.getValue().name} />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Project</span>,
          size: 200,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('myProjectsStatus', {
          cell: (status) => status.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Status</span>,
          maxSize: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: Object.values(CardStatusEnum).map((status) => ({
              label: status,
              value: status,
            })),
          },
          enableSorting: true,
        }),
        columnHelper.accessor('myProjectsStatusDate', {
          cell: (date) => <DateCellFromString value={date.getValue()} />,
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Match Status Date</span>
          ),
          maxSize: 10,
          enableColumnFilter: false,
          enableSorting: true,
        }),
      ] as ColumnDef<TeamMatchDetails>[],
    [corpClientFilterData, cohortsFilterData],
  );

  return (
    <AdminTable
      columns={columns}
      data={matchData}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isError={isError}
      filteredCount={adminMatchesCount}
      enableFiltering={true}
      exportOptions={{
        exportQuery: AdminMatchTeams.useGetAdminTeamsMatchingExportData,
        dataToExportFn: (data: AdminTeamMatchExportResponse) => data.matches,
        exportFileName: `matching_teams_export_${new Date().toISOString().replace(' ', '_')}.csv`,
      }}
    />
  );
};

export const TeamMatchLink = ({
  discoveryId,
  discoveryName,
}: {
  discoveryId: number;
  discoveryName?: string | null;
}) => {
  return (
    <Link
      className='text-brand-500 hover:underline'
      to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.MATCHING_TEAMS}/${discoveryId}`}
    >
      {discoveryName ? discoveryName : discoveryId}
    </Link>
  );
};
